import { loadContentData } from "@src/packages/content/loader";
import { getDataPointForMarkerName } from "@src/packages/content/biomarkers";
import type { ContentBlockId } from "@src/packages/content/types";
import { getBiomarkerIdentity } from "./biomarker-identity";
import type { TestResult, TestResultType } from "./hooks/useTestResults";
import type { ClinicalObservation } from "./normalized-results";

loadContentData();

export type CanonicalResultSourceKind =
  "standalone" | "uploaded-document" | "report-derived";

export type CanonicalResultSource = {
  /**
   * This is deliberately limited to states the current API can prove. A
   * panel/report/import source kind requires backend provenance fields and
   * must not be inferred from a result name.
   */
  kind: CanonicalResultSourceKind;
  /** API-declared origin; this is not a panel/report classification. */
  origin: TestResult["source"];
  ownership: TestResult["ownership"];
  ownerId: TestResult["ownerId"];
  providerId: TestResult["providerId"];
  sourceId: TestResult["sourceId"];
  verificationStatus: TestResult["verificationStatus"];
  verifiedAt: TestResult["verifiedAt"];
  verifiedBy: TestResult["verifiedBy"];
  verificationNotes: TestResult["verificationNotes"];
  attachment: TestResult["attachment"];
  /** Normalized API provenance. Null for legacy test-result records. */
  reportId: string | null;
  orderId: string | null;
  evidenceEntityIds: string[];
  /**
   * Maturity of the measurement only. It is derived from the generated client
   * types rather than restated, because a restated copy went stale the moment
   * the backend retired `corrected` (dashboard-api #1748): a correction is now
   * a successor observation that keeps its own maturity — normally `final` —
   * and states the correction through `supersedesObservationId`.
   */
  observationStatus: ClinicalObservation["status"] | null;
  reviewStatus: ClinicalObservation["reviewStatus"] | null;
  /** The predecessor this observation supersedes; the correction edge itself. */
  supersedesObservationId: string | null;
  correctionReason: string | null;
  legacyTestResultId: string | null;
};

export type CanonicalResultRange = {
  /** Lab-provided range, kept separate from educational typical ranges. */
  min: TestResult["rangeMin"];
  max: TestResult["rangeMax"];
  baseUnit: TestResult["baseUnit"];
  convertedValue: TestResult["convertedValue"];
};

export type CanonicalTestResult = {
  id: string;
  /** API clinical-entity identity, when the normalized resource provides it. */
  clinicalEntityId: string | null;
  dataPointId: ContentBlockId | null;
  identity: string;
  name: string | null;
  value: string;
  valueType: TestResult["valueType"];
  unit: string;
  date: string | null;
  createdAt: string | null;
  type: TestResultType | null;
  category: string | null;
  phaseId: string | null;
  cycleId: string | null;
  patientValue: string | null;
  doctorValue: string | null;
  patientAssessmentNormal: boolean | null;
  notes: string | null;
  clinicalAssessment: TestResult["clinicalAssessment"];
  clinicalAssessmentDetails: string | null;
  loincCode: string | null;
  labReferenceRange: CanonicalResultRange;
  source: CanonicalResultSource;
};

export type CanonicalResultHistory = {
  identity: string;
  dataPointId: ContentBlockId | null;
  results: CanonicalTestResult[];
  latest: CanonicalTestResult;
  latestFirst: CanonicalTestResult[];
  trend: "up" | "down" | "stable" | "none";
};

export function getCanonicalResultValue(result: TestResult): string {
  return (
    [result.doctorValue, result.patientValue, result.value].find(
      (value) => value !== null && value !== undefined && value !== "",
    ) ?? ""
  );
}

function compareResultDates(
  left: CanonicalTestResult,
  right: CanonicalTestResult,
) {
  const leftDate = new Date(left.date ?? left.createdAt ?? "").getTime();
  const rightDate = new Date(right.date ?? right.createdAt ?? "").getTime();
  return leftDate - rightDate;
}

export function getResultTrend(
  results: CanonicalTestResult[],
): CanonicalResultHistory["trend"] {
  if (results.length < 2) return "none";

  const previous = Number.parseFloat(results[results.length - 2]!.value);
  const current = Number.parseFloat(results[results.length - 1]!.value);
  if (!Number.isFinite(previous) || !Number.isFinite(current)) return "none";
  if (current > previous * 1.05) return "up";
  if (current < previous * 0.95) return "down";
  return "stable";
}

export function toCanonicalTestResult(result: TestResult): CanonicalTestResult {
  const dataPoint = result.name
    ? getDataPointForMarkerName(result.name)
    : undefined;
  const attachment = result.attachment ?? null;

  return {
    id: result.id,
    clinicalEntityId: null,
    dataPointId: dataPoint?.id ?? null,
    identity: getBiomarkerIdentity(result.name),
    name: result.name,
    value: getCanonicalResultValue(result),
    valueType: result.valueType,
    unit: result.unit ?? "",
    date: result.date,
    createdAt: result.createdAt,
    type: result.type,
    category:
      result.bloodTestCategory ??
      result.urineTestCategory ??
      result.geneticsTestCategory ??
      result.imagingTestCategory ??
      result.samplingTestCategory ??
      result.clinicalTestCategory ??
      result.semenFluidTestCategory ??
      result.biopsyTestCategory ??
      result.otherCategory ??
      null,
    phaseId: result.phaseId,
    cycleId: result.cycleId,
    patientValue: result.patientValue,
    doctorValue: result.doctorValue,
    patientAssessmentNormal: result.patientAssessmentNormal,
    notes: result.notes,
    clinicalAssessment: result.clinicalAssessment,
    clinicalAssessmentDetails: result.clinicalAssessmentDetails,
    loincCode: result.loincCode ?? null,
    labReferenceRange: {
      min: result.rangeMin ?? null,
      max: result.rangeMax ?? null,
      baseUnit: result.baseUnit ?? null,
      convertedValue: result.convertedValue ?? null,
    },
    source: {
      kind: attachment ? "uploaded-document" : "standalone",
      origin: result.source,
      ownership: result.ownership,
      ownerId: result.ownerId,
      providerId: result.providerId,
      sourceId: result.sourceId,
      verificationStatus: result.verificationStatus,
      verifiedAt: result.verifiedAt,
      verifiedBy: result.verifiedBy,
      verificationNotes: result.verificationNotes,
      attachment,
      reportId: null,
      orderId: null,
      evidenceEntityIds: [],
      observationStatus: null,
      reviewStatus: null,
      supersedesObservationId: null,
      correctionReason: null,
      legacyTestResultId: null,
    },
  };
}

export function groupCanonicalResults(
  results: CanonicalTestResult[],
): CanonicalResultHistory[] {
  const groups = new Map<string, CanonicalTestResult[]>();

  for (const result of results) {
    const group = groups.get(result.identity);
    if (group) {
      group.push(result);
    } else {
      groups.set(result.identity, [result]);
    }
  }

  return Array.from(groups.values()).map((group) => {
    const sorted = [...group].sort(compareResultDates);
    const latest = sorted[sorted.length - 1]!;
    return {
      identity: latest.identity,
      dataPointId: latest.dataPointId,
      results: sorted,
      latest,
      latestFirst: [...sorted].reverse(),
      trend: getResultTrend(sorted),
    };
  });
}

export function groupCanonicalTestResults(
  results: TestResult[],
): CanonicalResultHistory[] {
  return groupCanonicalResults(results.map(toCanonicalTestResult));
}
